import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Star, MapPin, Phone, Mail, Award, Clock, Users,
  ChevronRight, Facebook, Instagram, Linkedin, MessageCircle,
  CheckCircle, Building, Globe, Share2
} from 'lucide-react';
import Logo from './Logo';
import { getRealtorBySlug, getRealtorsByState, US_STATES } from '../data/realtors';

const RealtorDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [relatedRealtors, setRelatedRealtors] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    const realtorData = getRealtorBySlug(slug);
    if (realtorData) {
      setRealtor(realtorData);
      // Get related realtors from same state
      const related = getRealtorsByState(realtorData.state)
        .filter(r => r.id !== realtorData.id)
        .slice(0, 3);
      setRelatedRealtors(related);
    }
    window.scrollTo(0, 0);
  }, [slug]);

  const getStateName = (code) => {
    const state = US_STATES.find(s => s.code === code);
    return state ? state.name : code;
  };

  if (!realtor) {
    return (
      <div className="min-h-screen bg-[#0a0f1c] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white">
      <style>{`
        .gradient-text {
          background: linear-gradient(135deg, #10b981 0%, #34d399 50%, #6ee7b7 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .profile-card {
          background: linear-gradient(135deg, rgba(20, 27, 45, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .profile-image-large {
          border: 4px solid transparent;
          background: linear-gradient(#141b2d, #141b2d) padding-box,
                      linear-gradient(135deg, #10b981, #34d399, #6ee7b7) border-box;
        }

        .info-card {
          background: rgba(20, 27, 45, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: all 0.3s ease;
        }

        .info-card:hover {
          border-color: rgba(16, 185, 129, 0.3);
          background: rgba(16, 185, 129, 0.05);
        }

        .contact-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
          transition: all 0.3s ease;
        }

        .contact-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(16, 185, 129, 0.4);
        }

        .specialty-tag {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%);
          border: 1px solid rgba(16, 185, 129, 0.25);
        }

        .cert-badge {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(251, 191, 36, 0.08) 100%);
          border: 1px solid rgba(251, 191, 36, 0.25);
        }

        .social-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .social-btn:hover {
          background: rgba(16, 185, 129, 0.15);
          border-color: rgba(16, 185, 129, 0.4);
          transform: translateY(-2px);
        }

        .realtor-mini-card {
          background: rgba(20, 27, 45, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: all 0.3s ease;
        }

        .realtor-mini-card:hover {
          border-color: rgba(16, 185, 129, 0.3);
          transform: translateX(4px);
        }

        .about-text {
          line-height: 1.8;
        }

        .about-text p {
          margin-bottom: 1rem;
        }

        .modal-overlay {
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <Logo />
          <Link
            to="/realtor"
            className="text-white/80 hover:text-white font-medium transition-colors text-sm"
          >
            All Realtors
          </Link>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-20 px-6">
        <div className="max-w-6xl mx-auto py-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/realtor" className="hover:text-emerald-400 transition-colors">Realtors</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/realtor?state=${realtor.state}`} className="hover:text-emerald-400 transition-colors">
              {getStateName(realtor.state)}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{realtor.city}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Profile Header */}
          <div className="profile-card rounded-3xl overflow-hidden mb-8">
            {/* Cover gradient */}
            <div className="h-32 md:h-40 bg-gradient-to-r from-emerald-600/30 via-emerald-500/20 to-blue-600/30 relative">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10"></div>
            </div>

            {/* Profile Info */}
            <div className="px-6 md:px-10 pb-8 relative">
              {/* Profile Image */}
              <div className="absolute -top-16 md:-top-20 left-6 md:left-10">
                <img
                  src={realtor.photo}
                  alt={realtor.name}
                  className="profile-image-large w-32 h-32 md:w-40 md:h-40 rounded-full object-cover shadow-2xl"
                />
              </div>

              {/* Actions - top right */}
              <div className="flex justify-end pt-4 gap-3">
                <button className="social-btn p-3 rounded-xl">
                  <Share2 className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Name and Title */}
              <div className="pt-16 md:pt-20">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{realtor.name}</h1>
                    <p className="text-gray-400 text-lg mb-3">{realtor.title}</p>

                    {/* Location & License */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 text-gray-400">
                        <MapPin className="w-4 h-4 text-emerald-500" />
                        <span>{realtor.city}, {getStateName(realtor.state)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Building className="w-4 h-4 text-emerald-500" />
                        <span>License: {realtor.licenseNumber}</span>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-3 mt-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                        <span className="text-xl font-bold text-white">{realtor.rating}</span>
                      </div>
                      <span className="text-gray-500">({realtor.reviewCount} reviews)</span>
                    </div>
                  </div>

                  {/* Contact Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={`tel:${realtor.phone}`}
                      className="contact-btn flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold"
                    >
                      <Phone className="w-5 h-5" />
                      Call Now
                    </a>
                    <button
                      onClick={() => setShowContactForm(true)}
                      className="social-btn flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-medium"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Send Message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              <section className="profile-card rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <Users className="w-5 h-5 text-emerald-500" />
                  About {realtor.name.split(' ')[0]}
                </h2>
                <div className="about-text text-gray-300">
                  {realtor.about.split('\n\n').map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </section>

              {/* Specialties */}
              <section className="profile-card rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Specialties
                </h2>
                <div className="flex flex-wrap gap-3">
                  {realtor.specialties.map((specialty, idx) => (
                    <span key={idx} className="specialty-tag px-4 py-2 rounded-xl text-emerald-400 font-medium">
                      {specialty}
                    </span>
                  ))}
                </div>
              </section>

              {/* Certifications */}
              {realtor.certifications && realtor.certifications.length > 0 && (
                <section className="profile-card rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                    <Award className="w-5 h-5 text-yellow-500" />
                    Certifications
                  </h2>
                  <div className="space-y-3">
                    {realtor.certifications.map((cert, idx) => (
                      <div key={idx} className="cert-badge px-4 py-3 rounded-xl flex items-center gap-3">
                        <Award className="w-5 h-5 text-yellow-500" />
                        <span className="text-gray-300">{cert}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="profile-card rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="info-card rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">{realtor.yearsExperience}</div>
                      <div className="text-gray-500 text-sm">Years Experience</div>
                    </div>
                  </div>

                  <div className="info-card rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Building className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">{realtor.transactionsCompleted}</div>
                      <div className="text-gray-500 text-sm">Transactions Closed</div>
                    </div>
                  </div>

                  <div className="info-card rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Globe className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-white">{realtor.languages.join(', ')}</div>
                      <div className="text-gray-500 text-sm">Languages</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="profile-card rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Contact</h3>
                <div className="space-y-4">
                  <a
                    href={`tel:${realtor.phone}`}
                    className="info-card rounded-xl p-4 flex items-center gap-4 cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium">{realtor.phone}</div>
                      <div className="text-gray-500 text-sm">Phone</div>
                    </div>
                  </a>

                  <a
                    href={`mailto:${realtor.email}`}
                    className="info-card rounded-xl p-4 flex items-center gap-4 cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium break-all">{realtor.email}</div>
                      <div className="text-gray-500 text-sm">Email</div>
                    </div>
                  </a>
                </div>

                {/* Social Media */}
                {realtor.socialMedia && (
                  <div className="mt-6 pt-6 border-t border-gray-800">
                    <h4 className="text-sm text-gray-500 mb-4">Social Media</h4>
                    <div className="flex gap-3">
                      {realtor.socialMedia.facebook && (
                        <a href={realtor.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="social-btn p-3 rounded-xl">
                          <Facebook className="w-5 h-5 text-blue-400" />
                        </a>
                      )}
                      {realtor.socialMedia.instagram && (
                        <a href={realtor.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="social-btn p-3 rounded-xl">
                          <Instagram className="w-5 h-5 text-pink-400" />
                        </a>
                      )}
                      {realtor.socialMedia.linkedin && (
                        <a href={realtor.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="social-btn p-3 rounded-xl">
                          <Linkedin className="w-5 h-5 text-blue-500" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Related Realtors */}
              {relatedRealtors.length > 0 && (
                <div className="profile-card rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-white mb-6">
                    Other Realtors in {getStateName(realtor.state)}
                  </h3>
                  <div className="space-y-3">
                    {relatedRealtors.map(r => (
                      <Link
                        key={r.id}
                        to={`/realtor/${r.slug}`}
                        className="realtor-mini-card rounded-xl p-4 flex items-center gap-4"
                      >
                        <img
                          src={r.photo}
                          alt={r.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500/30"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{r.name}</div>
                          <div className="text-gray-500 text-sm">{r.city}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-white text-sm">{r.rating}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay">
          <div className="profile-card rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Send Message</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-500 hover:text-white transition-colors text-2xl"
              >
                &times;
              </button>
            </div>

            <div className="flex items-center gap-4 mb-6 p-4 bg-emerald-500/10 rounded-xl">
              <img
                src={realtor.photo}
                alt={realtor.name}
                className="w-14 h-14 rounded-full object-cover border-2 border-emerald-500"
              />
              <div>
                <div className="text-white font-semibold">{realtor.name}</div>
                <div className="text-gray-400 text-sm">{realtor.city}, {getStateName(realtor.state)}</div>
              </div>
            </div>

            <form className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Your Name *</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-[#141b2d] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email *</label>
                <input
                  type="email"
                  className="w-full px-4 py-3 bg-[#141b2d] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Phone Number</label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 bg-[#141b2d] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50"
                  placeholder="(123) 456-7890"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Message *</label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 bg-[#141b2d] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 resize-none"
                  placeholder="Hi, I'm looking for a home in..."
                ></textarea>
              </div>
              <button
                type="submit"
                className="contact-btn w-full py-4 rounded-xl text-white font-semibold"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto text-center text-gray-500 text-sm">
          © 2025 RapidRates.io - Your trusted platform for finding top real estate professionals
        </div>
      </footer>
    </div>
  );
};

export default RealtorDetailPage;
